import React, { useState, useEffect } from "react";
import {
  CustomerProfile,
  PaginatedResponse,
  BookingRecord,
  PaginationMeta,
} from "../../types";
import {
  getCustomers,
  getCustomerHistory,
  exportCustomersToExcel,
} from "../../services/api/admin/customers";
import {
  Search,
  Mail,
  Phone,
  User,
  ShoppingBag,
  X,
  Calendar,
  School,
  Building2,
  Loader2,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import Pagination from "../Pagination";
import Swal from "sweetalert2";

// --- Sub-component: Customer History Modal ---
const CustomerHistoryModal: React.FC<{
  email: string;
  customerId: string;
  onClose: () => void;
}> = ({ email, customerId, onClose }) => {
  const [history, setHistory] = useState<BookingRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch bookings for specific customer
    getCustomerHistory(customerId)
      .then((res) => {
        setHistory(res);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [customerId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div>
            <h3 className="font-bold text-lg text-gray-900">Riwayat Pesanan</h3>
            <p className="text-sm text-gray-500">{email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-0">
          {loading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-giz-primary" />
            </div>
          ) : history.length > 0 ? (
            <table className="w-full text-sm text-left">
              <thead className="bg-white text-gray-500 font-bold border-b border-gray-100 sticky top-0">
                <tr>
                  <th className="px-6 py-3">Tanggal</th>
                  <th className="px-6 py-3">Kode</th>
                  <th className="px-6 py-3">Item</th>
                  <th className="px-6 py-3 text-right">Total</th>
                  <th className="px-6 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {history.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-gray-600">{b.date}</td>
                    <td className="px-6 py-3 font-mono font-medium">
                      {b.code}
                    </td>
                    <td className="px-6 py-3">
                      {b.ticketType}{" "}
                      <span className="text-xs text-gray-400">({b.pax}x)</span>
                    </td>
                    <td className="px-6 py-3 text-right font-bold">
                      Rp {b.totalPrice.toLocaleString("id-ID")}
                    </td>
                    <td className="px-6 py-3 text-center">
                      <span
                        className={`text-[10px] px-2 py-1 rounded-full font-bold ${
                          b.status === "PAID" || b.status === "COMPLETED"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {b.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-gray-400">
              Belum ada riwayat pesanan.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
export const CustomerList: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse<CustomerProfile> | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<{
    email: string;
    id: string;
  } | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportCustomersToExcel();
      Swal.fire({
        icon: "success",
        title: "Export Berhasil!",
        text: "File Excel telah diunduh",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Export Gagal",
        text: "Terjadi kesalahan saat mengexport data",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Fetch Data
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      getCustomers(page, 5, searchQuery).then((res) => {
        setData(res);
        setLoading(false);
      });
    }, 500); // Debounce
    return () => clearTimeout(timer);
  }, [page, searchQuery]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const paginationMeta: PaginationMeta = data?.meta || {
    total: 0,
    perPage: 5,
    currentPage: page,
    lastPage: 1,
    firstPage: 1,
  };

  return (
    <div className="space-y-6 animate-fadeIn h-full flex flex-col">
      <div className="flex flex-col md:flex-row justify-between items-end gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Database Pelanggan (CRM)
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Kelola data pelanggan, sekolah, dan travel agent.
          </p>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-2 transition-colors shadow-sm disabled:opacity-50"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            Export Excel
          </button>
          <div className="relative flex-1 md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cari Nama / Email..."
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-giz-primary outline-none shadow-sm"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex-1 overflow-hidden flex flex-col min-h-[500px]">
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 font-bold border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4">Pelanggan</th>
                <th className="px-6 py-4">Kontak</th>
                <th className="px-6 py-4 text-center">Kunjungan</th>
                <th className="px-6 py-4 text-right">Total Belanja (LTV)</th>
                <th className="px-6 py-4 text-right">Terakhir Visit</th>
                <th className="px-6 py-4 text-center">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-6 py-4">
                      <div className="h-8 bg-gray-100 rounded animate-pulse"></div>
                    </td>
                  </tr>
                ))
              ) : data && data.data.length > 0 ? (
                data.data.map((c, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-gray-50 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900 flex items-center gap-2">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                            c.type === "School"
                              ? "bg-purple-100 text-purple-600"
                              : c.type === "Agent"
                              ? "bg-orange-100 text-orange-600"
                              : "bg-indigo-100 text-indigo-600"
                          }`}
                        >
                          {c.type === "School" ? (
                            <School className="w-4 h-4" />
                          ) : c.type === "Agent" ? (
                            <Building2 className="w-4 h-4" />
                          ) : c.name ? (
                            c.name.charAt(0)
                          ) : (
                            "?"
                          )}
                        </div>
                        {c.name || "Tanpa Nama"}
                      </div>
                      <div className="ml-10 mt-1 flex gap-1">
                        {c.type === "School" && (
                          <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold uppercase flex items-center gap-1">
                            Institusi
                          </span>
                        )}
                        {c.type === "Agent" && (
                          <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold uppercase flex items-center gap-1">
                            Travel Agent
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                        <Mail className="w-3 h-3" /> {c.email}
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Phone className="w-3 h-3" /> {c.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-block bg-gray-100 px-3 py-1 rounded-full font-bold text-gray-700">
                        {c.totalVisits}x
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-emerald-600">
                      Rp {c.totalSpend.toLocaleString("id-ID")}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-500">
                      {c.lastVisit}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() =>
                          setSelectedCustomer({ email: c.email, id: c.id })
                        }
                        className="p-2 text-giz-primary hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Lihat Riwayat"
                      >
                        <ShoppingBag className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-400"
                  >
                    Data tidak ditemukan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination Controls */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <Pagination
            meta={{ ...paginationMeta, currentPage: page }}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {selectedCustomer && (
        <CustomerHistoryModal
          email={selectedCustomer.email}
          customerId={selectedCustomer.id}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
};
